import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { locationApi } from '../services/locationApi';

/** "Ashdod / אשדוד" — searchable by either name, since Autocomplete filters on the label. */
const bilingualLabel = (o) => {
  if (!o) return '';
  const en = o.nameEn || '';
  return o.nameHe && o.nameHe !== en ? (en ? `${en} / ${o.nameHe}` : o.nameHe) : en;
};

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * Closed-list Country + City filter for admin tables.
 *
 * Countries come from the server's countries table. Cities come from `loadCities(countryId)`,
 * which each table points at its own "cities in use" endpoint, so every option returns rows.
 * Ids are the contract: `onChange({ countryId, cityId })` with '' for "any", which the callers'
 * URL / state writers already treat as "remove the filter".
 *
 * Picking a city first fills in its country; changing or clearing the country drops a city that
 * no longer belongs to it.
 *
 * Renders two siblings. `renderItem(node, key)` lets a Grid layout wrap each in its own
 * `<Grid item>`; a flex toolbar can leave it as the identity.
 */
export default function CountryCityFilter({
  countryId = '',
  cityId = '',
  onChange,
  loadCities,
  loadCountries = locationApi.getAllCountries,
  size = 'small',
  countrySx,
  citySx,
  renderItem = (node) => node,
}) {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingCities, setLoadingCities] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingCountries(true);
    Promise.resolve(loadCountries())
      .then((list) => { if (!cancelled) setCountries(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setCountries([]); })
      .finally(() => { if (!cancelled) setLoadingCountries(false); });
    return () => { cancelled = true; };
  }, [loadCountries]);

  useEffect(() => {
    let cancelled = false;
    setLoadingCities(true);
    Promise.resolve(loadCities(countryId || null))
      .then((list) => { if (!cancelled) setCities(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setCities([]); })
      .finally(() => { if (!cancelled) setLoadingCities(false); });
    return () => { cancelled = true; };
  }, [loadCities, countryId]);

  const country = useMemo(
    () => countries.find((c) => sameId(c.id, countryId)) || null,
    [countries, countryId],
  );
  const city = useMemo(
    () => cities.find((c) => sameId(c.id, cityId)) || null,
    [cities, cityId],
  );

  const handleCountry = (_, next) => {
    const nextCountryId = next?.id ?? '';
    const keepCity = city && next && sameId(city.countryId, next.id);
    onChange({ countryId: nextCountryId, cityId: keepCity ? cityId : '' });
  };

  const handleCity = (_, next) => {
    if (!next) {
      onChange({ countryId, cityId: '' });
      return;
    }
    onChange({ countryId: next.countryId ?? countryId, cityId: next.id });
  };

  const countryField = (
    <Autocomplete
      size={size}
      options={countries}
      value={country}
      onChange={handleCountry}
      getOptionLabel={bilingualLabel}
      isOptionEqualToValue={(a, b) => sameId(a?.id, b?.id)}
      loading={loadingCountries}
      sx={countrySx}
      renderInput={(params) => <TextField {...params} label="Country" />}
    />
  );
  const cityField = (
    <Autocomplete
      size={size}
      options={cities}
      value={city}
      onChange={handleCity}
      getOptionLabel={bilingualLabel}
      isOptionEqualToValue={(a, b) => sameId(a?.id, b?.id)}
      loading={loadingCities}
      noOptionsText={country ? 'No cities in use in this country' : 'No cities in use'}
      sx={citySx}
      renderInput={(params) => <TextField {...params} label="City" />}
    />
  );

  return (
    <>
      <React.Fragment key="country">{renderItem(countryField, 'country')}</React.Fragment>
      <React.Fragment key="city">{renderItem(cityField, 'city')}</React.Fragment>
    </>
  );
}
